import { Pressable, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Chip } from '../index';
import type { DropsetDraft, DropsetVariant } from '@heraion/shared';
import type { ConfirmFn } from '../seams';
import type { useTheme } from './theme';
import { makeStyles } from './loggerStyles';
import type { CardHints } from './cardHints';
import type { HoldProps } from './useTransientTip';

// Slice 4.4 — when the user activates "Set type" from ⋯ (or the set is
// already a dropset/myorep), reps/weight/rpe/× hide and the variant
// chips take their place inline. The drop steps below carry the
// per-step reps/weight.
//
// Slice 5 — picking a variant is delegated to the parent (onPickVariant):
// activating on a fresh set seeds the chain from the working set via
// proposeDropsetScheme and clears the parent inputs; swapping the variant
// on an existing chain just relabels it. Removing the chain (×) hands the
// top round back to the parent row (onRemoveChain).
export function SetTypeChipBar({
  dropset,
  inputsLocked,
  onPickVariant,
  onRemoveChain,
  onCloseTypeBar,
  hold,
  hints,
  confirm,
  theme,
}: {
  dropset: DropsetDraft | null;
  inputsLocked: boolean;
  onPickVariant: (variant: DropsetVariant) => void;
  onRemoveChain: () => void;
  onCloseTypeBar: () => void;
  /** RW-25 — the host's confirm seam. The × below discards logged drop steps,
   *  and react-native's Alert never fires on react-native-web. */
  confirm: ConfirmFn;
  /** #308: holding a chip pins its explanation in the card's balloon; the
   *  tap always picks. Owned by the FocusPanel. */
  hold: HoldProps;
  hints: CardHints;
  theme: ReturnType<typeof useTheme>;
}) {
  const { t } = useTranslation();
  const styles = makeStyles(theme);
  return (
    <>
      <Chip
        theme={theme}
        dense
        hitSlop={{ top: 8, bottom: 8 }}
        label={t('mobile.logger.setTypeDropset', { defaultValue: 'Dropset' })}
        selected={dropset?.variant === 'classical'}
        {...hold(hints.dropset)}
        onPress={() => onPickVariant('classical')}
        disabled={inputsLocked}
      />
      <Chip
        theme={theme}
        dense
        hitSlop={{ top: 8, bottom: 8 }}
        label={t('mobile.logger.setTypeMyorep', { defaultValue: 'Myo-rep' })}
        selected={dropset?.variant === 'myorep'}
        {...hold(hints.myorep)}
        onPress={() => onPickVariant('myorep')}
        disabled={inputsLocked}
      />
      {/* Dropset/Myo-rep explain themselves when held, like every other
          control on the card (#308). */}
      <View style={styles.dropsetControlSpacer} />
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={t('workout.dropsetRemove')}
        {...hold(hints.dropsetRemove).gesture}
        accessibilityHint={hints.dropsetRemove}
        onPress={() => {
          if (dropset === null) {
            onCloseTypeBar();
            return;
          }
          const hasData = dropset.steps.some(
            (st) =>
              st.reps.trim() !== '' ||
              st.weight.trim() !== '' ||
              st.completedAt !== null,
          );
          if (!hasData) {
            onRemoveChain();
            onCloseTypeBar();
            return;
          }
          void confirm({
            title: t('workout.dropsetRemove'),
            body: t('workout.dropsetRemoveConfirm', {
              defaultValue:
                'You have {{n}} drop steps. Convert back to a working set and discard them?',
              n: dropset.steps.length,
            }),
            confirmLabel: t('workout.dropsetRemove'),
            cancelLabel: t('workout.cancel'),
          }).then((ok) => {
            if (!ok) return;
            onRemoveChain();
            onCloseTypeBar();
          });
        }}
        disabled={inputsLocked}
        hitSlop={8}
        style={({ pressed }) => [
          styles.dropsetRemoveChip,
          pressed && styles.iconButtonPressed,
        ]}
      >
        <Text style={styles.dropsetRemoveChipText}>×</Text>
      </Pressable>
    </>
  );
}
